import type { DownloadJobType } from "@volgo/core/contract";

export type PollEnd = "settled" | "timeout" | "aborted";

export interface PollOptions {
  intervalMs: number;
  /** polling never goes on longer than this, whatever `step` answers */
  limitMs: number;
  signal: AbortSignal;
  now?: () => number;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  /** the timer that ends a `step` which does not answer by the deadline (default: a real timer) */
  expire?: (ms: number, signal: AbortSignal) => Promise<void>;
}

function defaultSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    }
    signal.addEventListener("abort", done);
  });
}

/**
 * Calls `step` once per interval while it answers "still waiting" (true).
 * Ends with `settled` when `step` answers false, `timeout` when `limitMs` has passed, `aborted` on the signal.
 *
 * The time limit holds in both ways a poll can go on for ever: it is checked before every call (a job that
 * never finishes), and a call that is still unanswered at the deadline is given up (a request that never
 * comes back). The abort signal ends an unanswered call the same way. `step` gets a signal that is aborted
 * as soon as its answer is no longer wanted; an answer that arrives after that must be dropped.
 */
export async function pollUntil(
  step: (signal: AbortSignal) => Promise<boolean>,
  options: PollOptions,
): Promise<PollEnd> {
  const now = options.now ?? Date.now;
  const sleep = options.sleep ?? defaultSleep;
  const expire = options.expire ?? defaultSleep;
  const deadline = now() + options.limitMs;
  for (;;) {
    await sleep(options.intervalMs, options.signal);
    if (options.signal.aborted) return "aborted";
    if (now() >= deadline) return "timeout";

    const call = new AbortController();
    const stop = () => call.abort();
    options.signal.addEventListener("abort", stop);
    let outcome: "waiting" | "settled" | "expired";
    try {
      const answer = step(call.signal).then(
        (waiting) => (waiting ? "waiting" : "settled") as "waiting" | "settled",
      );
      answer.catch(() => {
        // a failure that arrives after the call was given up has nobody to report to
      });
      outcome = await Promise.race([
        answer,
        expire(deadline - now(), call.signal).then(() => "expired" as const),
      ]);
    } finally {
      call.abort(); // ends the timer when the answer came first, and tells `step` when it did not
      options.signal.removeEventListener("abort", stop);
    }
    if (options.signal.aborted) return "aborted";
    if (outcome === "expired") return "timeout";
    if (outcome === "settled") return "settled";
  }
}

export function isActive(job: DownloadJobType): boolean {
  return job.status === "queued" || job.status === "running";
}
